/**
 * The bounded retry loop the upload pipeline wraps around its tRPC mutations
 * (session creation and finalize), and the rule for which rejections are worth
 * replaying.
 *
 * Both upload tasks — the signed-in recording and the anonymous funnel's take —
 * run their mutations through {@link runMutation} under the one predicate,
 * {@link isRetryableMutationError}. They differ only in whether anything may
 * cancel the call.
 */
import { TRPCClientError } from "@trpc/client";

import { readErrorData, type AppErrorData } from "../errors";
import { computeBackoffDelayMs, throwIfAborted } from "./resumable";
import { UploadAbortedError, type SleepFn } from "./types";

/**
 * Whether the server refused for a reason a fresh upload session reproduces
 * identically: the recording is over the cap, the monthly allowance is spent,
 * or the caller is not signed in.
 */
function isSessionIndependentRefusal(data: AppErrorData | undefined): boolean {
  return (
    data?.errorCode === "upload_too_large" ||
    data?.errorCode === "quota_exceeded" ||
    data?.code === "UNAUTHORIZED" ||
    data?.httpStatus === 401
  );
}

/**
 * Whether an upload mutation's rejection may be replayed — the signed-in
 * recording's and the anonymous funnel's alike (#898).
 *
 * Never retried: an `upload_too_large` or `quota_exceeded` code, an
 * `UNAUTHORIZED`/401, and any other 4xx. Retried: `httpStatus >= 500`, and any
 * failure that never produced a response (a `TRPCClientError` with no
 * `httpStatus`, or a non-tRPC error).
 *
 * The two codes are 403s, which the status test alone would already refuse.
 * They are named anyway so that the rule does not depend on the status the
 * server happens to send them with.
 */
export function isRetryableMutationError(error: unknown): boolean {
  if (!(error instanceof TRPCClientError)) {
    return true;
  }
  const data = readErrorData(error);
  if (isSessionIndependentRefusal(data)) {
    return false;
  }
  if (typeof data?.httpStatus === "number") {
    return data.httpStatus >= 500;
  }
  return true;
}

/**
 * Whether the user is offered a Retry after `finalize` was refused outright.
 *
 * A retry re-uploads the whole recording under a fresh session, so it is only
 * offered when a fresh session could change the answer. An over-cap recording,
 * a spent allowance and a signed-out caller all fail identically the next time
 * — a spent allowance until the month turns over — so they offer none. Every
 * other refusal does.
 */
export function finalizeTerminalCanRetry(error: unknown): boolean {
  return !isSessionIndependentRefusal(readErrorData(error));
}

/** True when `error` is the run's abort reason and must propagate rather than classify. */
export function isAbortReason(error: unknown, signal: AbortSignal): boolean {
  return error === signal.reason || error instanceof UploadAbortedError;
}

/** How a {@link runMutation} loop ended. */
export type MutationOutcome<T> =
  | { ok: true; value: T }
  /**
   * `exhausted` is true when every attempt failed with a retryable error, and
   * false when the server refused outright.
   */
  | { ok: false; error: unknown; exhausted: boolean };

/**
 * Run one mutation under the pipeline's bounded exponential backoff.
 *
 * `onAttempt` fires before every call, so a screen can show the attempt number
 * instead of a static spinner over silent retries.
 *
 * With a `signal`, the loop checks it before every attempt, rethrows its abort
 * reason instead of classifying it, and hands it to `sleep`, so an abort during
 * a backoff rejects out of the loop rather than issuing another call. Without
 * one, nothing can cancel the loop.
 *
 * @returns the value, or the last error and whether the budget ran out.
 */
export async function runMutation<T>(params: {
  /** The mutation to call. */
  call: () => Promise<T>;
  /** The total attempt budget, including the first call. */
  maxAttempts: number;
  /** The backoff clock. */
  sleep: SleepFn;
  /** Which rejections to replay; defaults to {@link isRetryableMutationError}. */
  isRetryable?: (error: unknown) => boolean;
  /** Called with the 1-based attempt number before every call. */
  onAttempt?: (attempt: number) => void;
  /** Cancels the loop; omit it for a mutation nothing may cancel. */
  signal?: AbortSignal;
}): Promise<MutationOutcome<T>> {
  const {
    call,
    maxAttempts,
    sleep,
    isRetryable = isRetryableMutationError,
    onAttempt,
    signal,
  } = params;
  // `sleep` always takes a signal; a loop nothing may cancel hands it one that
  // never fires.
  const sleepSignal = signal ?? new AbortController().signal;
  let lastError: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (signal !== undefined) throwIfAborted(signal);
    onAttempt?.(attempt);
    try {
      return { ok: true, value: await call() };
    } catch (error) {
      if (signal !== undefined && isAbortReason(error, signal)) {
        throw error;
      }
      lastError = error;
      if (!isRetryable(error)) {
        return { ok: false, error, exhausted: false };
      }
      if (attempt >= maxAttempts) {
        return { ok: false, error, exhausted: true };
      }
      await sleep(computeBackoffDelayMs(attempt), sleepSignal);
    }
  }
  return { ok: false, error: lastError, exhausted: true };
}
